/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import { useTranslation } from 'react-i18next'
import { CompanyContact } from 'types/entities/CompanyContact'

interface MergeCompaniesContactsListProps {
    contacts?: CompanyContact[]
    emptyLabel: string
    duplicateEmails?: Set<string>
}

const MergeCompaniesContactsList = ({
    contacts,
    emptyLabel,
    duplicateEmails,
}: MergeCompaniesContactsListProps) => {
    const { t } = useTranslation()

    if (!contacts || contacts.length === 0) {
        return (
            <div className="text-xs text-gray-400 italic bg-gray-100/50 dark:bg-gray-800/50 p-4 rounded-xl border border-dashed border-gray-200 dark:border-gray-700 text-center">
                {emptyLabel}
            </div>
        )
    }

    return (
        <div className="MergeCompaniesContactsList">
            {contacts.map((contact) => {
                const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim()
                const email = (contact.email || '').trim()
                const normalizedEmail = email.toLowerCase()
                const hasDuplicateEmail = !!email && !!duplicateEmails?.has(normalizedEmail)
                const hasNoContactDetails = !email && !contact.phone?.trim()

                return (
                    <div
                        key={contact.id}
                        className="bg-white dark:bg-gray-900 p-3 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm flex flex-col"
                    >
                        <div className="flex items-center justify-between gap-2">
                            <span className="font-bold text-gray-900 dark:text-white text-sm">
                                {fullName || t('Unnamed contact')}
                            </span>
                            {hasDuplicateEmail && (
                                <span className="text-[10px] px-2 py-0.5 rounded bg-orange-100 text-orange-800 font-semibold">
                                    {t('Duplicate email')}
                                </span>
                            )}
                        </div>
                        <div className="flex flex-col text-[11px] text-gray-500 mt-1">
                            <span>{email || '-'}</span>
                            <span>{contact.phone || '-'}</span>
                        </div>
                        {hasNoContactDetails && (
                            <span className="mt-1 text-[10px] text-red-600 font-medium">
                                {t('Missing contact details')}
                            </span>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

export default MergeCompaniesContactsList
